import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message } from '../types';
import { hashColor, getInitials } from '../utils';

interface Props {
  message: Message;
  align: 'left' | 'right';
}

export function MessageCard({ message, align }: Props) {
  const color = hashColor(message.from);
  const initials = getInitials(message.from);
  const isLeft = align === 'left';
  const [zoomed, setZoomed] = useState(false);

  return (
    <>
      <div className={`flex gap-3 mb-5 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1 select-none"
          style={{ backgroundColor: color }}
          aria-hidden
        >
          {initials}
        </div>

        <div className={`max-w-[82%] flex flex-col ${isLeft ? 'items-start' : 'items-end'}`}>
          <div
            className={`bg-[#1e1e38] border border-[#2d2d4e] px-4 py-3 ${
              isLeft ? 'rounded-2xl rounded-tl-sm' : 'rounded-2xl rounded-tr-sm'
            }`}
          >
            <div className="prose prose-invert prose-sm max-w-none break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.message}
              </ReactMarkdown>
            </div>
            {message.image_url && (
              <img
                src={message.image_url}
                alt="Attached image"
                className="mt-3 rounded-lg max-w-full max-h-64 object-contain cursor-zoom-in"
                loading="lazy"
                onClick={() => setZoomed(true)}
              />
            )}
          </div>
          <span className="text-slate-500 text-xs mt-1 px-1">{message.from}</span>
        </div>
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
          onClick={() => setZoomed(false)}
        >
          <button
            onClick={() => setZoomed(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 text-white text-xl hover:bg-black/80 transition-colors"
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={message.image_url!}
            alt="Attached image"
            className="max-h-screen max-w-screen w-auto sm:max-h-[80vh] object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
